import * as React from "react";
import {
  Card,
  CardHeader,
  Body1,
  Caption1,
  Title3,
  Button,
  tokens,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import {
  DocumentAdd24Regular,
  CommentAdd24Regular,
  TaskListAdd24Regular,
  Settings24Regular,
} from "@fluentui/react-icons";

interface Props {
  onPickCreate: () => void;
  onPickAddFollowup: () => void;
  onPickAddTask: () => void;
  onOpenSettings: () => void;
}

const useStyles = makeStyles({
  root: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  card: {
    cursor: "pointer",
    transitionProperty: "transform, box-shadow",
    transitionDuration: "120ms",
  },
  cardHover: {
    ":hover": {
      transform: "translateY(-1px)",
      boxShadow: tokens.shadow8,
    },
  },
  iconWrapper: {
    color: tokens.colorBrandForeground1,
  },
  footer: {
    marginTop: "8px",
    display: "flex",
    justifyContent: "flex-end",
  },
});

/**
 * Écran d'accueil : choix de l'action (créer ticket, suivi, tâche).
 */
export function ActionPicker({
  onPickCreate,
  onPickAddFollowup,
  onPickAddTask,
  onOpenSettings,
}: Props): React.ReactElement {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <Title3>Que veux-tu faire ?</Title3>

      <Card
        className={mergeClasses(styles.card, styles.cardHover)}
        onClick={onPickCreate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPickCreate();
          }
        }}
      >
        <CardHeader
          image={
            <span className={styles.iconWrapper} aria-hidden>
              <DocumentAdd24Regular />
            </span>
          }
          header={<Body1><strong>Créer un nouveau ticket</strong></Body1>}
          description={<Caption1>Depuis l'objet et le corps du mail.</Caption1>}
        />
      </Card>

      <Card
        className={mergeClasses(styles.card, styles.cardHover)}
        onClick={onPickAddFollowup}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPickAddFollowup();
          }
        }}
      >
        <CardHeader
          image={
            <span className={styles.iconWrapper} aria-hidden>
              <CommentAdd24Regular />
            </span>
          }
          header={<Body1><strong>Ajouter au ticket existant</strong></Body1>}
          description={<Caption1>Le mail devient un suivi d'un ticket déjà ouvert.</Caption1>}
        />
      </Card>

      <Card
        className={mergeClasses(styles.card, styles.cardHover)}
        onClick={onPickAddTask}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPickAddTask();
          }
        }}
      >
        <CardHeader
          image={
            <span className={styles.iconWrapper} aria-hidden>
              <TaskListAdd24Regular />
            </span>
          }
          header={<Body1><strong>Ajouter une tâche au ticket</strong></Body1>}
          description={
            <Caption1>Planifier une ou plusieurs tâches sur un ticket existant.</Caption1>
          }
        />
      </Card>

      <div className={styles.footer}>
        <Button appearance="subtle" icon={<Settings24Regular />} onClick={onOpenSettings}>
          Paramètres
        </Button>
      </div>
    </div>
  );
}
